import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import HyperModal from 'react-hyper-modal';
import { useTranslation } from 'react-i18next';
import { DocumentRenderer } from '@keystone-6/document-renderer';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { renderers } from '../../../utils/utilsDocumentRenderers';
import { AcceptConsentPolicyMutation } from '../../../graphql/consent/acceptConsentPolicyMutation';
import { GetConsentPoliciesQuery } from '../../../graphql/consent/getConsentPoliciesQuery';
import { useAuth } from '../../../lib/auth';
import { ConsentModalProps } from './props';

const END_OF_SCROLL_THRESHOLD_PX = 16;

export const ConsentModal = ({ isOpen, onClose, policies }: ConsentModalProps) => {
  const { t } = useTranslation();
  const { getCurrentUser } = useAuth();
  const { register, handleSubmit, watch, reset } = useForm();
  const policyIds = policies.map((policy) => policy.id);
  const { data, loading: isLoadingContent } = useQuery(GetConsentPoliciesQuery, {
    variables: { ids: policyIds },
    skip: !isOpen || policyIds.length === 0,
  });
  const policiesContent = data?.consentPolicies ?? [];
  const [acceptConsentPolicy] = useMutation(AcceptConsentPolicyMutation);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [reachedEndByPolicyId, setReachedEndByPolicyId] = useState<
    Record<string, boolean>
  >({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isMultiple = policies.length > 1;
  const activePolicy = policies[activeIndex];

  useEffect(() => {
    if (isOpen) {
      reset();
      setActiveIndex(0);
      setReachedEndByPolicyId({});
      setSubmitError(false);
    }
  }, [isOpen, reset]);

  const checkIfReachedEnd = () => {
    const el = scrollContainerRef.current;
    if (!el || isLoadingContent || !activePolicy) return;
    const isAtEnd =
      el.scrollHeight - el.scrollTop - el.clientHeight < END_OF_SCROLL_THRESHOLD_PX;
    if (isAtEnd) {
      setReachedEndByPolicyId((prev) =>
        prev[activePolicy.id] ? prev : { ...prev, [activePolicy.id]: true },
      );
    }
  };

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = 0;
    checkIfReachedEnd();
  }, [activeIndex, isLoadingContent, policiesContent.length]);

  const watchedValues = watch();
  const allChecked =
    policies.length > 0 &&
    policies.every((policy) => watchedValues[`policy-${policy.id}`]);
  const allReachedEnd = policies.every((policy) => reachedEndByPolicyId[policy.id]);
  const isLastPolicy = activeIndex === policies.length - 1;
  const activeChecked = !!activePolicy && !!watchedValues[`policy-${activePolicy.id}`];
  const activeReachedEnd = !!activePolicy && !!reachedEndByPolicyId[activePolicy.id];

  const goToNextPolicy = () => {
    setActiveIndex((index) => Math.min(index + 1, policies.length - 1));
  };

  const onSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      await acceptConsentPolicy({
        variables: { policyIds: policies.map((policy) => policy.id) },
      });
      // currentUser is plain React state (see lib/auth.tsx), not reactive
      // Apollo cache data — it only updates via an explicit getCurrentUser() call.
      await getCurrentUser();
      onClose();
    } catch (err) {
      console.log(err);
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const activeDocument = activePolicy
    ? policiesContent.find((p: { id: string }) => p.id === activePolicy.id)?.content
        ?.document
    : null;

  return (
    <HyperModal isOpen={isOpen} requestClose={onClose} portalMode>
      <div className="h-screen flex flex-col p-6 pt-12">
        <div className="text-xl text-primary font-bold text-center mb-4 shrink-0">
          {activePolicy?.title || t('consent.modalTitle')}
        </div>
        {isMultiple && (
          <div className="flex gap-2 justify-center mb-4 shrink-0">
            {policies.map((policy, index) => {
              const isChecked = !!watchedValues[`policy-${policy.id}`];
              const isActive = index === activeIndex;

              return (
                <button
                  key={policy.id}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={`w-9 h-9 shrink-0 rounded-full text-sm font-bold flex items-center justify-center border ${
                    isActive
                      ? 'bg-primary text-white border-primary'
                      : 'bg-gray-light text-gray-dark border-transparent'
                  }`}
                >
                  {isChecked ? '✓' : index + 1}
                </button>
              );
            })}
          </div>
        )}
        <div
          ref={scrollContainerRef}
          onScroll={checkIfReachedEnd}
          data-testid="consent-modal-scroll-container"
          className="flex-1 min-h-0 overflow-y-auto"
        >
          {isLoadingContent && (
            <div className="text-center text-sm text-gray-dark">
              {t('consent.loading')}
            </div>
          )}
          {activeDocument && (
            <DocumentRenderer document={activeDocument} renderers={renderers} />
          )}
        </div>
        {!isLoadingContent && activePolicy && (
          <div className="shrink-0 pt-4 flex flex-col gap-2">
            <Checkbox
              key={activePolicy.id}
              name={`policy-${activePolicy.id}`}
              register={register}
              validation={{ required: true }}
              disabled={!reachedEndByPolicyId[activePolicy.id]}
            >
              {t('consent.accept')}
            </Checkbox>
            {submitError && (
              <div className="text-danger text-sm text-center">
                {t('consent.submitError')}
              </div>
            )}
            <div className="flex justify-center">
              {isMultiple && !isLastPolicy ? (
                <Button
                  type="primary"
                  disabled={!activeReachedEnd || !activeChecked}
                  onClick={goToNextPolicy}
                >
                  {t('consent.nextDocument')}
                </Button>
              ) : (
                <Button
                  type="primary"
                  disabled={!allReachedEnd || !allChecked || isSubmitting}
                  loading={isSubmitting}
                  onClick={handleSubmit(onSubmit)}
                >
                  {t('consent.acceptButton')}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </HyperModal>
  );
};
